import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, FormField, FormHelp, Label, LabeledControl } from '@zillow/constellation-10';

import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import { postingConstraints } from '../../../../utils/formValidation';
import postingUtils, { multiFieldsUtil, ROOMMATE_GENDER_PREFERENCE } from '../../../../utils/postingUtils';
import wizardActions from '../../../Wizard/actions';

import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';

const propTypes = {};
const defaultProps = {};

// the key that holds all of the child keys for the checkboxes
const fieldName = 'gender';

const GENDER_PREFERENCE_MULTI_KEY = {
  HAS_NO_ROOMMATE_GENDER_PREFERENCE: 'hasNoRoommateGenderPreference',
  HAS_ROOMMATE_GENDER_PREFERENCE_FEMALE: 'hasRoommateGenderPreferenceFemale',
  HAS_ROOMMATE_GENDER_PREFERENCE_MALE: 'hasRoommateGenderPreferenceMale',
  HAS_ROOMMATE_GENDER_PREFERENCE_NON_BINARY: 'hasRoommateGenderPreferenceNonBinary',
};

const GenderPreferenceFormField = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { isInModalState, isSaveComplete, saveOutsideComponent, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    listingAlias,
    roomForRent: {
      roommateRequirements: { roommateGenderPreferences },
    },
  } = useSelector((state) => state.properties.currentProperty);

  const [errorDisplay, setErrorDisplay] = useState('');

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      gender: {
        hasNoRoommateGenderPreference: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.NO_PREFERENCE),
        hasRoommateGenderPreferenceFemale: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.FEMALE),
        hasRoommateGenderPreferenceMale: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.MALE),
        hasRoommateGenderPreferenceNonBinary: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.NON_BINARY),
      },
    },
    postingConstraints,
  );

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: fieldName,
          value: inputs[fieldName],
        },
      });
    }
  }, [isSaveComplete]);

  const getUpdatedState = (key) => {
    const newObj = {
      ...inputs.gender,
    };

    // if key is anything other than no preference
    if (key !== GENDER_PREFERENCE_MULTI_KEY.HAS_NO_ROOMMATE_GENDER_PREFERENCE) {
      // set no preference to false
      newObj.hasNoRoommateGenderPreference = false;
    }

    switch (key) {
      case GENDER_PREFERENCE_MULTI_KEY.HAS_NO_ROOMMATE_GENDER_PREFERENCE:
        newObj.hasRoommateGenderPreferenceFemale = false;
        newObj.hasRoommateGenderPreferenceMale = false;
        newObj.hasRoommateGenderPreferenceNonBinary = false;
        newObj.hasNoRoommateGenderPreference = true;
        break;

      case GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_FEMALE:
        newObj.hasRoommateGenderPreferenceFemale = !inputs.gender.hasRoommateGenderPreferenceFemale;
        break;

      case GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_MALE:
        newObj.hasRoommateGenderPreferenceMale = !inputs.gender.hasRoommateGenderPreferenceMale;
        break;

      case GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_NON_BINARY:
        newObj.hasRoommateGenderPreferenceNonBinary = !inputs.gender.hasRoommateGenderPreferenceNonBinary;
        break;

      default:
        break;
    }

    let foundTrue = false;
    for (const [key, value] of Object.entries(newObj)) {
      // if key is anything other than no preference and has a value
      if (key !== GENDER_PREFERENCE_MULTI_KEY.HAS_NO_ROOMMATE_GENDER_PREFERENCE && value === true) {
        foundTrue = true;
      }
    }

    // if nothing other than no preferences was not true
    if (!foundTrue) {
      // set no preferences to true
      newObj.hasNoRoommateGenderPreference = true;
    }

    return newObj;
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    const isSelectionEmpty = isInModalState && !Object.values(value).some((value) => value);
    const allowedEmptyErrorDisplay = isSelectionEmpty ? 'Enter your choice.' : '';
    setErrorDisplay(allowedEmptyErrorDisplay);

    if (allowedEmptyErrorDisplay || hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    // saveOutsideComponent check must be above updateIsFetchingData to keep next and prev functionality
    if (saveOutsideComponent) {
      actions.addFieldDataEntry({
        reducerDispatch,
        entryObj: {
          saveToParentKey: 'roomForRent',
          fieldName,
          value: inputs[fieldName],
        },
      });

      return;
    }

    const dataToSave = {
      property: {
        listingAlias,
        roomForRent: {
          ...value,
        },
      },
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        updateCurrentProperty(value);

        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err) => {
        updateServerError(err?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateCurrentProperty = (value) => {
    const roommateGenderPreferences = postingUtils.getRoommateGenderPreferences(value);

    dispatch(
      PropertiesActions.updateCurrentPropertyRoommateRequirements({
        roommateGenderPreferences,
      }),
    );
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), [saveOutsideComponent]);

  const handleChange = (event) => {
    event.persist();
    const { name } = event.target;
    const newObj = getUpdatedState(name);

    const mockEvent = {
      target: {
        name: fieldName,
        value: newObj,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    saveDebounced(mockEvent);
  };

  return (
    <div data-testid="GenderPreferenceFormField">
      {serverErrorObj.fieldName === fieldName && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <FormField
        label={<Label required={false}>Gender</Label>}
        error={!!errorDisplay}
        description={<FormHelp error={!!errorDisplay}>{errorDisplay}</FormHelp>}
      >
        <LabeledControl
          label={<Label>No preference</Label>}
          control={
            <Checkbox
              name={GENDER_PREFERENCE_MULTI_KEY.HAS_NO_ROOMMATE_GENDER_PREFERENCE}
              checked={inputs.gender.hasNoRoommateGenderPreference === true}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          label={<Label>Female</Label>}
          control={
            <Checkbox
              name={GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_FEMALE}
              checked={inputs.gender.hasRoommateGenderPreferenceFemale === true}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          label={<Label>Male</Label>}
          control={
            <Checkbox
              name={GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_MALE}
              checked={inputs.gender.hasRoommateGenderPreferenceMale === true}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          label={<Label>Non-binary</Label>}
          control={
            <Checkbox
              name={GENDER_PREFERENCE_MULTI_KEY.HAS_ROOMMATE_GENDER_PREFERENCE_NON_BINARY}
              checked={inputs.gender.hasRoommateGenderPreferenceNonBinary === true}
              onChange={handleChange}
            />
          }
        />
      </FormField>
    </div>
  );
};

GenderPreferenceFormField.propTypes = propTypes;
GenderPreferenceFormField.defaultProps = defaultProps;

export default GenderPreferenceFormField;
